import CTNButton from '@ctn/ui/components/ctn-button';
import CTNButtonsGroup from '@ctn/ui/components/ctn-buttons-group';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNMessage from '@ctn/ui/components/ctn-message';
import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNUpload from '@ctn/ui/components/ctn-upload';
import { useEffect, type ReactElement } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { isSolicitanteOrigin } from '@/components/DocumentosListado/columns';
import DocViewer from '@/components/DocViewer';
import ModalSubsanacionDocumentos from '@/components/ModalSubsanacionDocumentos';
import PageHeader from '@/components/PageHeader';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useFetchDocument from '@/hooks/useFetchDocument';
import useLoadTramite from '@/hooks/useLoadTramite';
import { usePdfViewer } from '@/hooks/usePdfViewer';
import useRouteTramite from '@/hooks/useRouteTramite';

import { ROUTES_LABEL } from '@/constants/routes.constants';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import { notificationsConfig } from '@/helpers/notifications';
import { commonUpload, getIconDocumento } from '@/helpers/upload';
import { type FormTramiteValues, type IDocumento, type IDocumentoForm } from '@/interfaces/model/don.interface';

type Form = {
	documento: IDocumento;
	origen: 'Solicitante' | 'Notaría';
};

/**
 * [REQUERIDO] Enviar la url parametros idTramite y idDocumento: /solicitudes/:idTramite/documentacion/:idDocumento
 * [OPCIONAL] En el caso de querer subsanar el documento se envia mediante params en la url: /solicitudes/:idTramite/documentacion/:idDocumento/:idTipoSubsanacion/:idSubsanacion
 */
const TramiteDocumentacionDetalle = (): ReactElement => {
	const { idTramite, idTipoSubsanacion, idSubsanacion, idDocumento } = useParams();	
	const { isShowing, openModal, closeModal } = useModal({});
	const { watch: watchGlobal, getValues } = useFormContext<FormTramiteValues>();
	const isSubsanable = Boolean(idTramite && idTipoSubsanacion && idSubsanacion);
	const location = useLocation();

	const navigate = useNavigate();
	const {
		user,
		getTramiteData,
		maestrosTramite: { configuracionTramite },
	} = useGlobalContext();
	const { hasData } = useLoadTramite(Number(idTramite), true);
	const ROUTES_TRAMITE = useRouteTramite();
	const { appendBreadcrumb, onBackClick } = usePageContext();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });

	const isRepresentante = location?.state?.representante;
	const idPersonaJuridica = Number(location?.state?.idPersonaJuridica);
	const idRepresentante = location?.state?.idRepresentante;

	const dataTramite = getValues();
	const dataSujeto = dataTramite?.sujetos?.find((element) => element?.id === idPersonaJuridica);
	const dataRepresentante = dataSujeto?.representantes?.find((element)=> element?.id === idRepresentante);
	const documentoPoder = dataRepresentante?.documentoPoder;
	
	const fetchArgs = isRepresentante
		? { idTramite: Number(idTramite), idPersonaJuridica, idRepresentante }
		: { idTramite: Number(idTramite) };

	const { fetchDocument } = useFetchDocument(fetchArgs);

	const { isLoading, url, loadPdf } = usePdfViewer();

	const { reset, watch, control, setValue } = useForm<Form>({
		defaultValues: {
			documento: undefined,
			origen: 'Solicitante'
		},
		mode: 'onChange',
	});

	const documento = watch('documento');

	const getData = async (documentos: IDocumentoForm[]) => {
		if (!idDocumento || !user) {
			openNotification(
				notificationTemplate.errorMessage('No se pudo cargar la información necesaria para el detalle.'),
			);
			return;
		}

		if (idDocumento) {
			const getPdfSource = async () => {
				if (!idDocumento) return undefined;
				return (await fetchDocument(Number(idDocumento)))?.data;
			};

			loadPdf(getPdfSource);

			const documento = isRepresentante ? documentoPoder : documentos.find(({ id }) => id === Number(idDocumento));
			reset({ documento });
		}
	};

	const onSubmit = () => {
		closeModal();
		navigate(`${ROUTES_TRAMITE.SOLICITUDES}/${idTramite}`);
	};

	useEffect(() => {
		appendBreadcrumb('Documento', [
			getBreadcrumbTramite(getTramiteData()),
			{ label: ROUTES_LABEL.SOLICITUDES, url: ROUTES_TRAMITE.SOLICITUDES },
			{ label: ROUTES_LABEL.SOLICITUD, url: `${ROUTES_TRAMITE.SOLICITUDES}/${idTramite}` },
			{ label: 'Documento' },
		]);
	}, []);

	useEffect(() => {
		if (!documento) return;
		setValue('origen', isSolicitanteOrigin(documento?.origenDocumento) ? 'Solicitante' : 'Notaría');
	}, [documento]);

	useEffect(() => {
		if (hasData) {
			const documentos = watchGlobal('documentos');
			getData(documentos);
		}
	}, [hasData]);

	if (!documento) return <></>;

	return (
		<>
			<PageHeader hasHelp title={ROUTES_LABEL.DOCUMENTACION_DETALLE} className="u-mt-8" />
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 u-flow-border ] ">
				<div className="c-pnp-box__body">
					{isSubsanable && (
						<CTNMessage
							content={
								<>
									<p>
										<b>Documento pendiente de subsanación.</b>
									</p>
									<p>
										Motivos indicados por el notario:{' '}
										{documento?.subsanacionPendiente?.observacionesPeticion}.
									</p>
								</>
							}
							className="u-mt-24"
							icon="warning"
							status="warning"
							mode="outline"
							closable={false}
						/>
					)}
					<div className="o-grid ">
						<div className="o-grid__row">
							<div className="o-grid__col-12 o-grid__col-4@portrait">
								<CTNUpload
									{...commonUpload}
									displayOnly
									noFeedBack
									condensed
									maxFileSize={configuracionTramite?.pesoMaximoFichero ?? 0}
									classNameField={isSubsanable ? 'u-mt-32' : ''}
									label="Documento"
									fileLoaded={
										documento?.nombre
											? { name: documento?.nombre, icon: getIconDocumento(documento) }
											: undefined
									}
								/>
							</div>
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12 o-grid__col-4@portrait"
								name='origen'
								label="Origen"
								control={control}
							/>
						</div>
						{!isSolicitanteOrigin(documento?.origenDocumento) && (
							<div className="o-grid__row">
								<CTNInput
									noFeedBack
									condensed
									displayOnly
									type="text"
									classNameField="[ o-grid__col-12 o-grid__col-12@portrait ] [ u-mt-24 ]"
									name='documento.observaciones'
									label="Observaciones de la notaría"
									control={control}
								/>
							</div>
						)}
					</div>
					<DocViewer
						mimeType={documento.mimeType.nombre}
						loading={isLoading}
						className="u-mt-32"
						url={url}
						fileIcon={getIconDocumento(documento)}
						fileName={documento.nombre}
						view="FitH"
					/>
				</div>
				<footer className="c-pnp-box__footer">
					<CTNButtonsGroup align="right">
						<CTNButton
							label={isSubsanable ? 'Cancelar' : 'Cerrar'}
							mode="tertiary"
							minWidth="140"
							size="md"
							onClick={() => onBackClick()}
						/>
						{isSubsanable && (
							<CTNButton label="Subsanar" mode="primary" minWidth="140" size="md" onClick={openModal} />
						)}
					</CTNButtonsGroup>
				</footer>
			</div>
			{isSubsanable && (
				<ModalSubsanacionDocumentos
					hasErrorDoc
					data={{
						idTramite: Number(idTramite),
						idTipo: Number(idTipoSubsanacion),
						id: Number(idSubsanacion),
					}}
					idTipoDocumento={documento.tipoDocumento.id}
					isOpen={isShowing}
					onClose={closeModal}
					onSubmit={onSubmit}
				/>
			)}
		</>
	);
};

export default TramiteDocumentacionDetalle;
